/**
 * Search Component - Expandable search input with keyboard shortcuts
 *
 * @example Basic usage
 * <Search
 *   value={searchQuery}
 *   onChange={e => setSearchQuery(e.target.value)}
 *   placeholder="Buscar clientes"
 * />
 *
 * @example With loading and results count
 * <Search
 *   value={searchQuery}
 *   onChange={e => setSearchQuery(e.target.value)}
 *   loading={isLoading}
 *   resultsCount={filteredItems.length}
 * />
 *
 * @example Disable keyboard shortcut hint
 * <Search
 *   value={searchQuery}
 *   onChange={e => setSearchQuery(e.target.value)}
 *   showShortcutHint={false}
 * />
 *
 * @example Custom shortcut key
 * <Search
 *   value={searchQuery}
 *   onChange={e => setSearchQuery(e.target.value)}
 *   shortcutKey="k"
 * />
 *
 * Props:
 * - value (string): Controlled input value
 * - onChange (function): Change handler, receives event with e.target.value
 * - placeholder (string): Input placeholder, default "Buscar"
 * - loading (boolean): Shows spinner when true
 * - resultsCount (number): Displays "X resultados" badge when searching
 * - showShortcutHint (boolean): Shows "/" hint when collapsed, default true
 * - shortcutKey (string): Key to focus search from anywhere, default "/"
 */
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Icon, Spin } from 'antd';

var SearchContainer = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 12px;
  position: relative;
`;

var SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  background: ${function(props) { return props.isExpanded ? '#ffffff' : '#fafafa'; }};
  border-radius: 24px;
  padding: 4px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1.5px solid ${function(props) { return props.isFocused ? '#722ed1' : (props.isExpanded ? '#e8e8e8' : 'transparent'); }};
  box-shadow: ${function(props) {
    if (props.isFocused) return '0 0 0 3px rgba(114, 46, 209, 0.1), 0 2px 8px rgba(0, 0, 0, 0.08)';
    if (props.isExpanded) return '0 2px 8px rgba(0, 0, 0, 0.06)';
    return 'none';
  }};

  &:hover {
    background: ${function(props) { return props.isExpanded ? '#ffffff' : '#f0f0f0'; }};
    border-color: ${function(props) { return props.isFocused ? '#722ed1' : (props.isExpanded ? '#d9d9d9' : 'transparent'); }};
    box-shadow: ${function(props) {
      if (props.isFocused) return '0 0 0 3px rgba(114, 46, 209, 0.1), 0 2px 8px rgba(0, 0, 0, 0.08)';
      return '0 2px 8px rgba(0, 0, 0, 0.06)';
    }};
  }
`;

var SearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: ${function(props) { return props.isFocused ? '#722ed1' : '#8c8c8c'; }};
  font-size: 15px;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.2s ease;

  &:hover {
    color: ${function(props) { return props.isFocused ? '#722ed1' : '#595959'; }};
  }

  &:focus {
    outline: none;
  }
`;

var InputWrapper = styled.div`
  overflow: hidden;
  width: ${function(props) { return props.isExpanded ? '200px' : '0'; }};
  opacity: ${function(props) { return props.isExpanded ? 1 : 0; }};
  transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
`;

var StyledInput = styled.input`
  width: 100%;
  height: 30px;
  border: none;
  background: transparent;
  padding: 0 8px;
  font-size: 14px;
  color: #262626;
  outline: none;

  &::placeholder {
    color: #bfbfbf;
  }
`;

var ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: #f0f0f0;
  color: #8c8c8c;
  font-size: 10px;
  cursor: pointer;
  margin-right: 4px;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: #e8e8e8;
    color: #595959;
  }

  &:focus {
    outline: none;
  }
`;

var ShortcutHint = styled.span`
  display: ${function(props) { return props.isExpanded ? 'none' : 'flex'; }};
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #bfbfbf;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 1px 6px;
  margin-right: 6px;
  font-family: monospace;
  transition: opacity 0.2s ease;
`;

var ResultsBadge = styled.span`
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #722ed1;
  background: #f9f0ff;
  border-radius: 10px;
  padding: 2px 8px;
  margin-right: 6px;
  white-space: nowrap;
  font-weight: 500;
`;

var LoadingWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

var Search = function(props) {
  var value = props.value || '';
  var onChange = props.onChange;
  var placeholder = props.placeholder || 'Buscar';
  var loading = props.loading || false;
  var resultsCount = props.resultsCount;
  var showShortcutHint = props.showShortcutHint !== false;
  var shortcutKey = props.shortcutKey || '/';

  var inputRef = useRef(null);
  var blurTimeoutRef = useRef(null);
  var containerRef = useRef(null);
  var [isExpanded, setIsExpanded] = useState(false);
  var [isFocused, setIsFocused] = useState(false);

  // Keyboard shortcut handler
  useEffect(function() {
    var handleKeyDown = function(e) {
      // Don't trigger if user is typing in an input/textarea
      var activeElement = document.activeElement;
      var isInputActive = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );

      if (e.key === shortcutKey && !isInputActive) {
        e.preventDefault();
        setIsExpanded(true);
        setTimeout(function() {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 50);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return function() {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [shortcutKey]);

  var handleSearchClick = function() {
    setIsExpanded(true);
    setTimeout(function() {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };

  var handleInputFocus = function() {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    setIsExpanded(true);
    setIsFocused(true);
  };

  var handleInputBlur = function() {
    setIsFocused(false);
    blurTimeoutRef.current = setTimeout(function() {
      if (!value) {
        setIsExpanded(false);
      }
    }, 150);
  };

  var handleInputChange = function(e) {
    if (onChange) {
      onChange(e);
    }
  };

  var handleClear = function() {
    if (onChange) {
      onChange({ target: { value: '' } });
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  var handleKeyDown = function(e) {
    if (e.key === 'Escape') {
      if (onChange) {
        onChange({ target: { value: '' } });
      }
      setIsExpanded(false);
      setIsFocused(false);
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  var showExpanded = isExpanded || Boolean(value);
  var showResultsCount = resultsCount !== undefined && resultsCount !== null && value;

  return (
    <SearchContainer ref={containerRef} role="search">
      <SearchWrapper isExpanded={showExpanded} isFocused={isFocused}>
        <SearchButton
          type="button"
          onClick={handleSearchClick}
          isFocused={isFocused}
          aria-label="Buscar"
        >
          <Icon type="search" />
        </SearchButton>
        <InputWrapper isExpanded={showExpanded}>
          <StyledInput
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            placeholder={placeholder}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            aria-label={placeholder}
          />
        </InputWrapper>
        {loading && (
          <LoadingWrapper>
            <Spin size="small" />
          </LoadingWrapper>
        )}
        {showResultsCount && !loading && (
          <ResultsBadge>
            {resultsCount} {resultsCount === 1 ? 'resultado' : 'resultados'}
          </ResultsBadge>
        )}
        {value && !loading && (
          <ClearButton
            type="button"
            onClick={handleClear}
            aria-label="Limpar busca"
          >
            <Icon type="close" />
          </ClearButton>
        )}
        {showShortcutHint && (
          <ShortcutHint isExpanded={showExpanded}>{shortcutKey}</ShortcutHint>
        )}
      </SearchWrapper>
    </SearchContainer>
  );
};

export default Search;
